"use client";

import { c as _c } from "react/compiler-runtime";
import React, { createContext, useContext, useEffect, useRef, useState, ReactNode, useCallback, useMemo } from "react";
import useCapacitor from "@/hooks/useCapacitor";
import { useAndroidKeyboard } from "@/hooks/useAndroidKeyboard";

// Define the different spaces that need to be measured
interface LayoutSpaces {
  // Space used by persistent UI elements (header, breadcrumb, etc)
  headerSpace: number;

  // Space used by dynamic elements (pinned waves, etc)
  pinnedSpace: number;

  // Space used by tab elements when present
  tabsSpace: number;

  // Space used by spacer elements
  spacerSpace: number;

  // Space used by mobile tabs
  mobileTabsSpace: number;

  // Space used by mobile nav
  mobileNavSpace: number;
  // Available space for content
  contentSpace: number;

  // Whether measurements have completed
  measurementsComplete: boolean;
}
type View = "wave" | "leaderboard" | "winners" | "myVotes" | "outcome" | "faq" | "notifications" | "myStreamFeed" | "mobileWaves" | "mobileAbout";
const spacesAreEqual = (a: LayoutSpaces, b: LayoutSpaces) => a.headerSpace === b.headerSpace && a.pinnedSpace === b.pinnedSpace && a.tabsSpace === b.tabsSpace && a.spacerSpace === b.spacerSpace && a.mobileTabsSpace === b.mobileTabsSpace && a.mobileNavSpace === b.mobileNavSpace && a.contentSpace === b.contentSpace && a.measurementsComplete === b.measurementsComplete;

// Helper function to calculate height style
const calculateHeightStyle = (view: View, spaces: LayoutSpaces, capacitorSpace: number): React.CSSProperties => {
  // Use visual viewport height when available, with a 100vh fallback.
  const heightCalc = `calc(var(--app-height, 100vh) - ${spaces.headerSpace}px - ${spaces.pinnedSpace}px - ${spaces.tabsSpace}px - ${spaces.spacerSpace}px - ${spaces.mobileTabsSpace}px - ${spaces.mobileNavSpace}px - ${capacitorSpace}px)`;
  return {
    height: heightCalc,
    maxHeight: heightCalc
  };
};

// Context type definition
// Define valid ref types for type safety
type LayoutRefType = "header" | "pinned" | "tabs" | "spacer" | "mobileTabs" | "mobileNav";
interface LayoutContextType {
  // Calculated spaces
  spaces: LayoutSpaces;

  // Registration system
  registerRef: (refType: LayoutRefType, element: HTMLDivElement | null) => void;

  // Pre-calculated styles for main containers
  contentContainerStyle: React.CSSProperties;

  // Style for chat view
  waveViewStyle: React.CSSProperties;

  // Style for leaderboard view
  leaderboardViewStyle: React.CSSProperties;

  // Style for winners view
  winnersViewStyle: React.CSSProperties;

  // Style for outcome view
  outcomeViewStyle: React.CSSProperties;

  // Style for my votes view
  myVotesViewStyle: React.CSSProperties;

  // Style for FAQ view
  faqViewStyle: React.CSSProperties;

  // Style for notifications view
  notificationsViewStyle: React.CSSProperties;

  // Style for feed view
  myStreamFeedStyle: React.CSSProperties;

  // Style for homepage feed view (excludes header/breadcrumbs, includes tabs)
  homepageFeedStyle: React.CSSProperties;

  // Style for small screen layout feed (includes header and tabs)
  smallScreenFeedStyle: React.CSSProperties;

  // Style for mobile waves view
  mobileWavesViewStyle: React.CSSProperties;

  // Style for mobile about view
  mobileAboutViewStyle: React.CSSProperties;
}

// Default context values
const defaultSpaces: LayoutSpaces = {
  headerSpace: 0,
  pinnedSpace: 0,
  tabsSpace: 0,
  spacerSpace: 0,
  mobileTabsSpace: 0,
  mobileNavSpace: 0,
  contentSpace: 0,
  measurementsComplete: false
};

// Create context
const LayoutContext = createContext<LayoutContextType>({
  spaces: defaultSpaces,
  registerRef: () => {},
  // No-op for default value
  contentContainerStyle: {},
  // Empty style object as default
  waveViewStyle: {},
  // Empty style object as default
  leaderboardViewStyle: {},
  // Empty style object as default
  winnersViewStyle: {},
  // Empty style object as default
  myVotesViewStyle: {},
  // Empty style object as default
  outcomeViewStyle: {},
  // Empty style object as default
  faqViewStyle: {},
  // Empty style object as default for FAQ
  notificationsViewStyle: {},
  // Empty style object as default
  myStreamFeedStyle: {},
  // Empty style object as default
  homepageFeedStyle: {},
  // Empty style object as default
  smallScreenFeedStyle: {},
  // Empty style object as default
  mobileWavesViewStyle: {},
  // Empty style object as default
  mobileAboutViewStyle: {} // Empty style object as default
});

// Provider component
export const LayoutProvider: React.FC<{
  children: ReactNode;
}> = t0 => {
  const $ = _c(68);
  const {
    children
  } = t0;
  const {
    isCapacitor,
    isAndroid,
    isIos
  } = useCapacitor();
  const {
    isVisible: isKeyboardVisible
  } = useAndroidKeyboard();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  useEffect(_temp, t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      header: null,
      pinned: null,
      tabs: null,
      spacer: null,
      mobileTabs: null,
      mobileNav: null
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const refMap = useRef(t2);
  const resizeObserverRef = useRef(null);
  const [spaces, setSpaces] = useState(defaultSpaces);
  const calculateSpacesRef = useRef(_temp2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = (refType, element) => {
      if (refMap.current[refType] === element) {
        return;
      }
      const previousElement = refMap.current[refType];
      refMap.current[refType] = element;
      if (resizeObserverRef.current) {
        if (previousElement) {
          resizeObserverRef.current.unobserve(previousElement);
        }
        if (element) {
          resizeObserverRef.current.observe(element);
        }
      }
      requestAnimationFrame(() => calculateSpacesRef.current());
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const registerRef = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      let headerHeight = 0;
      let pinnedHeight = 0;
      let tabsHeight = 0;
      let spacerHeight = 0;
      let mobileTabsHeight = 0;
      let mobileNavHeight = 0;
      const headerElement = refMap.current.header;
      const pinnedElement = refMap.current.pinned;
      const tabsElement = refMap.current.tabs;
      const spacerElement = refMap.current.spacer;
      const mobileTabsElement = refMap.current.mobileTabs;
      const mobileNavElement = refMap.current.mobileNav;
      if (headerElement) {
        ;
        try {
          const rect = headerElement.getBoundingClientRect();
          headerHeight = rect.height;
        } catch (t5) {
          const e = t5;
          console.error("Error measuring header element:", e);
        }
      }
      if (pinnedElement) {
        ;
        try {
          const rect_0 = pinnedElement.getBoundingClientRect();
          pinnedHeight = rect_0.height;
        } catch (t6) {
          const e_0 = t6;
          console.error("Error measuring pinned element:", e_0);
        }
      }
      if (tabsElement) {
        ;
        try {
          const rect_1 = tabsElement.getBoundingClientRect();
          tabsHeight = rect_1.height;
        } catch (t7) {
          const e_1 = t7;
          console.error("Error measuring tabs element:", e_1);
        }
      }
      if (spacerElement) {
        ;
        try {
          const rect_2 = spacerElement.getBoundingClientRect();
          spacerHeight = rect_2.height;
        } catch (t8) {
          const e_2 = t8;
          console.error("Error measuring spacer element:", e_2);
        }
      }
      if (mobileTabsElement) {
        ;
        try {
          const rect_3 = mobileTabsElement.getBoundingClientRect();
          mobileTabsHeight = rect_3.height;
        } catch (t9) {
          const e_3 = t9;
          console.error("Error measuring mobile tabs element:", e_3);
        }
      }
      if (mobileNavElement) {
        ;
        try {
          const rect_4 = mobileNavElement.getBoundingClientRect();
          mobileNavHeight = rect_4.height;
        } catch (t10) {
          const e_4 = t10;
          console.error("Error measuring mobile nav element:", e_4);
        }
      }
      const totalOccupiedSpace = headerHeight + pinnedHeight + tabsHeight + spacerHeight + mobileTabsHeight + mobileNavHeight;
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
      const calculatedContentSpace = Math.max(0, viewportHeight - totalOccupiedSpace);
      const nextSpaces = {
        headerSpace: headerHeight,
        pinnedSpace: pinnedHeight,
        tabsSpace: tabsHeight,
        spacerSpace: spacerHeight,
        mobileTabsSpace: mobileTabsHeight,
        mobileNavSpace: mobileNavHeight,
        contentSpace: calculatedContentSpace,
        measurementsComplete: true
      };
      setSpaces(prevSpaces => spacesAreEqual(prevSpaces, nextSpaces) ? prevSpaces : nextSpaces);
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const calculateSpaces = t4;
  let t5;
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      resizeObserverRef.current = new ResizeObserver(() => {
        requestAnimationFrame(calculateSpaces);
      });
      Object.entries(refMap.current).forEach(t7 => {
        const [, element_0] = t7;
        if (element_0) {
          resizeObserverRef.current?.observe(element_0);
        }
      });
      window.addEventListener("resize", calculateSpaces);
      calculateSpaces();
      return () => {
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
          resizeObserverRef.current = null;
        }
        window.removeEventListener("resize", calculateSpaces);
      };
    };
    t6 = [calculateSpaces];
    $[4] = t5;
    $[5] = t6;
  } else {
    t5 = $[4];
    t6 = $[5];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      calculateSpacesRef.current = calculateSpaces;
    };
    t8 = [calculateSpaces];
    $[6] = t7;
    $[7] = t8;
  } else {
    t7 = $[6];
    t8 = $[7];
  }
  useEffect(t7, t8);
  let t9;
  bb0: {
    if (!spaces.measurementsComplete) {
      let t10;
      if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
        t10 = {};
        $[8] = t10;
      } else {
        t10 = $[8];
      }
      t9 = t10;
      break bb0;
    }
    const t10 = `calc(var(--app-height, 100vh) - ${spaces.headerSpace}px - ${spaces.spacerSpace}px)`;
    let t11;
    if ($[9] !== t10) {
      t11 = {
        height: t10,
        display: "flex"
      };
      $[9] = t10;
      $[10] = t11;
    } else {
      t11 = $[10];
    }
    t9 = t11;
  }
  const contentContainerStyle = t9;
  let t10;
  bb1: {
    if (!spaces.measurementsComplete) {
      let t11;
      if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
        t11 = {};
        $[11] = t11;
      } else {
        t11 = $[11];
      }
      t10 = t11;
      break bb1;
    }
    let capSpace = 0;
    if (isAndroid) {
      capSpace = isKeyboardVisible ? 0 : 128;
    } else {
      if (isIos || isCapacitor) {
        capSpace = 20;
      }
    }
    let t11;
    if ($[12] !== capSpace || $[13] !== spaces) {
      const adjustedSpaces = {
        ...spaces,
        mobileNavSpace: 0
      };
      t11 = calculateHeightStyle("wave", adjustedSpaces, capSpace);
      $[12] = capSpace;
      $[13] = spaces;
      $[14] = t11;
    } else {
      t11 = $[14];
    }
    const style = t11;
    if (isAndroid) {
      let t12;
      if ($[15] !== style) {
        t12 = {
          ...style,
          transition: "height 75ms ease-out, max-height 75ms ease-out"
        };
        $[15] = style;
        $[16] = t12;
      } else {
        t12 = $[16];
      }
      t10 = t12;
      break bb1;
    }
    t10 = style;
  }
  const waveViewStyle = t10;
  let t11;
  bb2: {
    if (!spaces.measurementsComplete) {
      let t12;
      if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
        t12 = {};
        $[17] = t12;
      } else {
        t12 = $[17];
      }
      t11 = t12;
      break bb2;
    }
    let t12;
    if ($[18] !== spaces) {
      t12 = calculateHeightStyle("leaderboard", spaces, 0);
      $[18] = spaces;
      $[19] = t12;
    } else {
      t12 = $[19];
    }
    t11 = t12;
  }
  const leaderboardViewStyle = t11;
  let t12;
  bb3: {
    if (!spaces.measurementsComplete) {
      let t13;
      if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
        t13 = {};
        $[20] = t13;
      } else {
        t13 = $[20];
      }
      t12 = t13;
      break bb3;
    }
    let t13;
    if ($[21] !== spaces) {
      t13 = calculateHeightStyle("winners", spaces, 0);
      $[21] = spaces;
      $[22] = t13;
    } else {
      t13 = $[22];
    }
    t12 = t13;
  }
  const winnersViewStyle = t12;
  let t13;
  bb4: {
    if (!spaces.measurementsComplete) {
      let t14;
      if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
        t14 = {};
        $[23] = t14;
      } else {
        t14 = $[23];
      }
      t13 = t14;
      break bb4;
    }
    let t14;
    if ($[24] !== spaces) {
      t14 = calculateHeightStyle("myVotes", spaces, 0);
      $[24] = spaces;
      $[25] = t14;
    } else {
      t14 = $[25];
    }
    t13 = t14;
  }
  const myVotesViewStyle = t13;
  let t14;
  bb5: {
    if (!spaces.measurementsComplete) {
      let t15;
      if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
        t15 = {};
        $[26] = t15;
      } else {
        t15 = $[26];
      }
      t14 = t15;
      break bb5;
    }
    let t15;
    if ($[27] !== spaces) {
      t15 = calculateHeightStyle("outcome", spaces, 0);
      $[27] = spaces;
      $[28] = t15;
    } else {
      t15 = $[28];
    }
    t14 = t15;
  }
  const outcomeViewStyle = t14;
  let t15;
  bb6: {
    if (!spaces.measurementsComplete) {
      let t16;
      if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
        t16 = {};
        $[29] = t16;
      } else {
        t16 = $[29];
      }
      t15 = t16;
      break bb6;
    }
    let t16;
    if ($[30] !== spaces) {
      t16 = calculateHeightStyle("faq", spaces, 0);
      $[30] = spaces;
      $[31] = t16;
    } else {
      t16 = $[31];
    }
    t15 = t16;
  }
  const faqViewStyle = t15;
  let t16;
  bb7: {
    if (!spaces.measurementsComplete) {
      let t17;
      if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
        t17 = {};
        $[32] = t17;
      } else {
        t17 = $[32];
      }
      t16 = t17;
      break bb7;
    }
    let t17;
    if ($[33] !== spaces) {
      t17 = calculateHeightStyle("notifications", spaces, 0);
      $[33] = spaces;
      $[34] = t17;
    } else {
      t17 = $[34];
    }
    t16 = t17;
  }
  const notificationsViewStyle = t16;
  let t17;
  bb8: {
    if (!spaces.measurementsComplete) {
      let t18;
      if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
        t18 = {};
        $[35] = t18;
      } else {
        t18 = $[35];
      }
      t17 = t18;
      break bb8;
    }
    let t18;
    if ($[36] !== spaces) {
      t18 = calculateHeightStyle("myStreamFeed", spaces, 0);
      $[36] = spaces;
      $[37] = t18;
    } else {
      t18 = $[37];
    }
    t17 = t18;
  }
  const myStreamFeedStyle = t17;
  let t18;
  bb9: {
    if (!spaces.measurementsComplete) {
      let t19;
      if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
        t19 = {};
        $[38] = t19;
      } else {
        t19 = $[38];
      }
      t18 = t19;
      break bb9;
    }
    let t19;
    if ($[39] !== spaces) {
      const homepageSpaces = {
        ...spaces,
        headerSpace: 0,
        spacerSpace: 0
      };
      t19 = calculateHeightStyle("myStreamFeed", homepageSpaces, 0);
      $[39] = spaces;
      $[40] = t19;
    } else {
      t19 = $[40];
    }
    t18 = t19;
  }
  const homepageFeedStyle = t18;
  let t19;
  bb10: {
    if (!spaces.measurementsComplete) {
      let t20;
      if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
        t20 = {};
        $[41] = t20;
      } else {
        t20 = $[41];
      }
      t19 = t20;
      break bb10;
    }
    const totalOffset = spaces.headerSpace + spaces.pinnedSpace + spaces.tabsSpace + spaces.spacerSpace + spaces.mobileTabsSpace + spaces.mobileNavSpace;
    const heightCalc = `calc(var(--app-height, 100vh) - ${totalOffset}px)`;
    let t20;
    if ($[42] !== heightCalc) {
      t20 = {
        height: heightCalc,
        maxHeight: heightCalc
      };
      $[42] = heightCalc;
      $[43] = t20;
    } else {
      t20 = $[43];
    }
    t19 = t20;
  }
  const smallScreenFeedStyle = t19;
  let t20;
  bb11: {
    if (!spaces.measurementsComplete) {
      let t21;
      if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
        t21 = {};
        $[44] = t21;
      } else {
        t21 = $[44];
      }
      t20 = t21;
      break bb11;
    }
    let t21;
    if ($[45] !== spaces) {
      t21 = calculateHeightStyle("mobileWaves", spaces, 0);
      $[45] = spaces;
      $[46] = t21;
    } else {
      t21 = $[46];
    }
    t20 = t21;
  }
  const mobileWavesViewStyle = t20;
  let t21;
  bb12: {
    if (!spaces.measurementsComplete) {
      let t22;
      if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
        t22 = {};
        $[47] = t22;
      } else {
        t22 = $[47];
      }
      t21 = t22;
      break bb12;
    }
    let t22;
    if ($[48] !== spaces) {
      t22 = calculateHeightStyle("mobileAbout", spaces, 0);
      $[48] = spaces;
      $[49] = t22;
    } else {
      t22 = $[49];
    }
    t21 = t22;
  }
  const mobileAboutViewStyle = t21;
  let t22;
  if ($[50] !== contentContainerStyle || $[51] !== faqViewStyle || $[52] !== homepageFeedStyle || $[53] !== leaderboardViewStyle || $[54] !== mobileAboutViewStyle || $[55] !== mobileWavesViewStyle || $[56] !== myStreamFeedStyle || $[57] !== myVotesViewStyle || $[58] !== notificationsViewStyle || $[59] !== outcomeViewStyle || $[60] !== smallScreenFeedStyle || $[61] !== spaces || $[62] !== waveViewStyle || $[63] !== winnersViewStyle) {
    t22 = {
      spaces,
      registerRef,
      contentContainerStyle,
      waveViewStyle,
      leaderboardViewStyle,
      winnersViewStyle,
      myVotesViewStyle,
      outcomeViewStyle,
      faqViewStyle,
      notificationsViewStyle,
      myStreamFeedStyle,
      homepageFeedStyle,
      smallScreenFeedStyle,
      mobileWavesViewStyle,
      mobileAboutViewStyle
    };
    $[50] = contentContainerStyle;
    $[51] = faqViewStyle;
    $[52] = homepageFeedStyle;
    $[53] = leaderboardViewStyle;
    $[54] = mobileAboutViewStyle;
    $[55] = mobileWavesViewStyle;
    $[56] = myStreamFeedStyle;
    $[57] = myVotesViewStyle;
    $[58] = notificationsViewStyle;
    $[59] = outcomeViewStyle;
    $[60] = smallScreenFeedStyle;
    $[61] = spaces;
    $[62] = waveViewStyle;
    $[63] = winnersViewStyle;
    $[64] = t22;
  } else {
    t22 = $[64];
  }
  const contextValue = t22;
  let t23;
  if ($[65] !== children || $[66] !== contextValue) {
    t23 = <LayoutContext.Provider value={contextValue}>{children}</LayoutContext.Provider>;
    $[65] = children;
    $[66] = contextValue;
    $[67] = t23;
  } else {
    t23 = $[67];
  }
  return t23;
};

// Custom hook to use the layout context
export const useLayout = () => {
  return useContext(LayoutContext);
};
function _temp() {
  const doc = document.documentElement;
  const viewport = window.visualViewport;
  const updateAppHeight = () => {
    const height = viewport?.height ?? window.innerHeight;
    doc.style.setProperty("--app-height", `${height}px`);
  };
  updateAppHeight();
  viewport?.addEventListener("resize", updateAppHeight);
  viewport?.addEventListener("scroll", updateAppHeight);
  window.addEventListener("resize", updateAppHeight);
  return () => {
    viewport?.removeEventListener("resize", updateAppHeight);
    viewport?.removeEventListener("scroll", updateAppHeight);
    window.removeEventListener("resize", updateAppHeight);
  };
}
function _temp2() {}